<script setup lang="ts">
import { computed, ref } from "vue";
import "@maersk-global/mds-components-core/mc-icon";
import "@maersk-global/mds-components-core/mc-tooltip";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import { ROUTES } from "@/router/constants";
import { translationKeyTransform } from "@/utils/translations-key-transformation";
import { useSidebarState } from "@/compositions/useSidebarState";
import { useFiltersStore } from "@/store";
import { userAnalyticsService } from "@/services";
import { Modal as IsceModal } from "@/components/Modal";
import type { NavigationLink } from "@/components/SideNavigation";

defineProps<{
  links: NavigationLink[];
}>();

const { t, locale } = useI18n();
const route = useRoute();
const { isCollapsed, expand } = useSidebarState();
const { resetAppliedFilters } = useFiltersStore();

const isRtl = computed(() => locale.value === "ar");

const prefixMatchRoutes = new Set([ROUTES.RISK_MANAGEMENT, ROUTES.REPORTING, ROUTES.CARRIER_PERFORMANCE]);

const isActive = (link: NavigationLink): boolean =>
  prefixMatchRoutes.has(link.route) ? route.path.startsWith(link.route) : route.path === link.route;

const label = (link: NavigationLink): string => t(translationKeyTransform(link.name));

const tooltipPosition = computed(() => (isRtl.value ? "left" : "right"));

const handleClick = (): void => {
  if (isCollapsed.value) expand();
  resetAppliedFilters();
};

// --- Locked upsell ---

const PRODUCT_BENEFITS: Record<string, string> = {
  DASHBOARD: "DASHBOARD_BENEFIT",
  SHIPMENTS: "SHIPMENTS_BENEFIT",
  AIR_SHIPMENTS: "AIR_SHIPMENTS_BENEFIT",
  "D&D import": "DND_IMPORT_BENEFIT",
  "D&D": "DND_BENEFIT",
  PORT_CONGESTION: "PORT_CONGESTION_BENEFIT",
  EMISSIONS: "EMISSIONS_BENEFIT",
  MAERSK_RISK_MANAGEMENT: "MAERSK_RISK_MANAGEMENT_BENEFIT",
  "CARRIER_PERFORMANCE.TITLE": "CARRIER_PERFORMANCE_BENEFIT",
  REPORTING: "REPORTING_BENEFIT"
};

const isLockedModalOpen = ref(false);
const selectedLockedFeatureName = ref("");
const selectedLockedVideoUrl = ref("");

const getEmbedVideoUrl = (url: string): string => {
  if (!url) return "";
  if (url.includes("youtube.com/watch")) {
    const videoId = url.split("v=")[1]?.split("&")[0];
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    return url.replace("youtube.com/watch?v=", "youtube.com/embed/");
  }
  if (url.includes("youtu.be/")) {
    const videoId = url.split("youtu.be/")[1]?.split("?")[0];
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    return url.replace("youtu.be/", "youtube.com/embed/");
  }
  return url;
};

const closeModal = (): void => {
  isLockedModalOpen.value = false;
};

const handleEmailClick = (): void => {
  userAnalyticsService.trackEvent("Upsell: Email Button Clicked", {
    featureName: selectedLockedFeatureName.value
  });
};

const handleLockedClick = (link: NavigationLink): void => {
  userAnalyticsService.trackEvent("Upsell: Locked Feature Clicked", {
    featureName: link.name,
    featureId: link.route
  });
  selectedLockedFeatureName.value = link.name;
  selectedLockedVideoUrl.value = link.videoUrl || "https://www.youtube.com/embed/TI4J_PLDY-w";
  isLockedModalOpen.value = true;
};
</script>

<template>
  <nav
    :class="['sidebar-nav', { 'sidebar-nav--collapsed': isCollapsed }]"
    :dir="isRtl ? 'rtl' : 'ltr'"
    aria-label="Main navigation"
  >
    <ul class="sidebar-nav__list">
      <li v-for="(link, index) in links" :key="link.route" class="sidebar-nav__item">
        <template v-if="link.isEnabled !== false">
          <mc-tooltip :position="tooltipPosition" fit="small" appearance="neutral" :zindex="9999">
            <div slot="trigger" class="sidebar-nav__trigger">
              <router-link
                :to="link.route"
                :data-test="`sidebar-nav/link-${index}`"
                :class="[
                  'sidebar-nav__link',
                  { 'sidebar-nav__link--active': isActive(link) },
                  { 'sidebar-nav__link--rtl': isRtl }
                ]"
                :aria-current="isActive(link) ? 'page' : undefined"
                @click="handleClick"
              >
                <span class="sidebar-nav__icon-wrap">
                  <mc-icon :icon="link.icon" size="20" class="sidebar-nav__icon" />
                </span>
                <span class="sidebar-nav__label mds-text--small-normal">
                  {{ label(link) }}
                </span>
              </router-link>
            </div>
            <span v-if="isCollapsed">{{ label(link) }}</span>
          </mc-tooltip>
        </template>
        <template v-else>
          <mc-tooltip :position="tooltipPosition" fit="small" appearance="neutral" :zindex="9999">
            <div
              slot="trigger"
              class="sidebar-nav__trigger"
              :data-test="`sidebar-nav/link-locked-${index}`"
              @click="handleLockedClick(link)"
            >
              <div :class="['sidebar-nav__link', 'sidebar-nav__link--locked', { 'sidebar-nav__link--rtl': isRtl }]">
                <span class="sidebar-nav__icon-wrap sidebar-nav__icon-wrap--lockable">
                  <mc-icon :icon="link.icon" size="20" class="sidebar-nav__icon" />
                  <mc-icon v-if="isCollapsed" icon="lock" size="16" class="sidebar-nav__lock-badge" />
                </span>
                <span class="sidebar-nav__label mds-text--small-normal">
                  {{ label(link) }}
                </span>
                <mc-icon v-if="!isCollapsed" icon="lock" size="16" class="sidebar-nav__lock-icon" />
              </div>
            </div>
            <span v-if="isCollapsed">{{ label(link) }}</span>
          </mc-tooltip>
        </template>
      </li>
    </ul>
  </nav>

  <isce-modal
    :is-open="isLockedModalOpen"
    :heading="`Unlock ${t(translationKeyTransform(selectedLockedFeatureName))}`"
    height="auto"
    :buttons="[{ label: 'Got it', value: 'close', primary: true }]"
    style="
      --mds_brand_appearance_primary_default_background-color: var(--mds_brand_appearance_primary_default_link-color);
      --mds_brand_appearance_primary_strong_background-color: var(--mds_brand_appearance_primary_default_link-color);
    "
    @close="closeModal"
    @click="closeModal"
  >
    <template #body>
      <div class="upsell-modal-body">
        <div
          class="upsell-video"
          style="
            margin-top: 4px;
            border-radius: 8px;
            overflow: hidden;
            display: flex;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          "
        >
          <iframe
            v-if="isLockedModalOpen"
            width="100%"
            height="200"
            :src="getEmbedVideoUrl(selectedLockedVideoUrl)"
            title="Maersk Premium Features"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
        <div class="upsell-benefit">
          <mc-icon icon="star" class="benefit-icon"></mc-icon>
          <p class="benefit-text">
            <b>{{
              PRODUCT_BENEFITS[selectedLockedFeatureName] ? t(PRODUCT_BENEFITS[selectedLockedFeatureName]) : ""
            }}</b>
          </p>
        </div>
        <div class="upsell-action">
          <p class="action-text">Interested? Get in touch with our team and we'll help you get started:</p>
          <a href="mailto:SVP_Support@lns.maersk.com" class="action-email" @click="handleEmailClick">
            <mc-icon icon="envelope" class="email-icon"></mc-icon>
            svp_support@lns.maersk.com
          </a>
        </div>
      </div>
    </template>
  </isce-modal>
</template>

<style scoped lang="scss">
@import "@/styles/colors_mds.scss";

.sidebar-nav {
  width: 100%;

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__item {
    width: 100%;
    padding-inline: 8px;

    .app-sidebar--collapsed & {
      padding-inline: 0;
    }
  }

  &__trigger {
    display: block;
    width: 100%;

    .app-sidebar--collapsed & {
      width: 56px;
    }
  }

  &__link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-block: 8px;
    padding-inline: 10px;
    border-radius: var(--mds_brand_border_large_radius, 8px);
    text-decoration: none !important;
    color: $wrapper--mds_brand_appearance_neutral_default_text-color !important;
    cursor: pointer;
    transition: background-color var(--mds_global_transition_fast_duration, 0.2s) ease-in-out;

    .app-sidebar--collapsed & {
      width: 56px;
      padding-inline: 0;
      justify-content: center;
      border-radius: 0;
      gap: 0;
    }

    &:hover:not(&--active) {
      background-color: $wrapper--mds_brand_appearance_neutral_weak_background-color;

      .sidebar-nav__icon {
        transform: scale(1.08) translateX(2px);
      }

      .app-sidebar--collapsed & .sidebar-nav__icon {
        transform: scale(1.08);
      }
    }

    &:focus-visible {
      outline: 2px solid $wrapper--mds_brand_appearance_primary_weak_background-color;
      outline-offset: 2px;
    }

    &--rtl {
      flex-direction: row-reverse;
    }

    &--locked {
      cursor: pointer;
      opacity: 0.6;

      &:hover {
        background-color: $wrapper--mds_brand_appearance_neutral_weak_background-color;
        opacity: 1;
      }
    }

    &--active {
      background-color: $wrapper--mds_brand_appearance_primary_weak_background-color;
      pointer-events: none;
      font-weight: 500;

      .sidebar-nav__label {
        color: $wrapper--mds_brand_appearance_primary_default_text-color;
      }
    }
  }

  &__icon-wrap {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
  }

  &__icon {
    display: block;
    transition: transform var(--mds_global_transition_fast_duration, 0.2s) cubic-bezier(0.4, 0, 0.2, 1);

    mc-icon::part(icon) {
      fill: $wrapper--mds_brand_appearance_neutral_default_text-color;
    }
  }

  &__label {
    white-space: nowrap;
    overflow: hidden;
    max-width: 180px;
    opacity: 1;
    transition:
      max-width var(--mds_global_transition_fast_duration, 0.2s) cubic-bezier(0.4, 0, 0.2, 1),
      opacity 120ms ease;

    .app-sidebar--collapsed & {
      max-width: 0;
      opacity: 0;
    }
  }

  &__lock-icon {
    margin-inline-start: auto;
    flex-shrink: 0;
    opacity: 0.7;
  }

  &__lock-badge {
    position: absolute;
    bottom: -4px;
    inset-inline-end: -6px;
    transform: scale(0.625);
    transform-origin: bottom right;
    opacity: 0.85;
  }

  &__icon-wrap--lockable {
    position: relative;
    overflow: visible;
  }
}

.upsell-modal-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.upsell-benefit {
  display: flex;
  align-items: flex-start;
  gap: 8px;

  .benefit-icon {
    flex-shrink: 0;
    color: $wrapper--mds_brand_appearance_primary_default_text-color;
  }

  .benefit-text {
    margin: 0;
  }
}

.upsell-action {
  .action-text {
    margin: 0 0 8px;
    font-size: 0.875rem;
  }

  .action-email {
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--mds_brand_appearance_primary_default_link-color);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .sidebar-nav__icon,
  .sidebar-nav__label,
  .sidebar-nav__link {
    transition: none;
  }
}
</style>
